import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react'
import UnsavedChangesModal from '../UnsavedChangesModal/UnsavedChangesModal.component'

type LeaveAction = () => void

let historyPopClaimed = false

const sameDocumentTarget = (url: string | URL | null | undefined): boolean => {
  if (url == null || url === '') return true
  try {
    const next = new URL(String(url), window.location.href)
    const current = new URL(window.location.href)
    return next.pathname === current.pathname && next.search === current.search
  } catch {
    return true
  }
}

const useCommunicationsLeaveGuard = (
  when: boolean,
): {
  requestLeave: (action: LeaveAction) => void
  modal: ReactElement
} => {
  const whenRef = useRef(when)
  whenRef.current = when
  const bypassRef = useRef(false)
  const pendingRef = useRef<LeaveAction | null>(null)
  const openRef = useRef(false)
  const lockedHref = useRef('')
  const lockedState = useRef<unknown>(null)
  const [open, setOpen] = useState(false)
  openRef.current = open

  const stay = useCallback(() => {
    pendingRef.current = null
    setOpen(false)
  }, [])

  const confirmLeave = useCallback(() => {
    const pending = pendingRef.current
    pendingRef.current = null
    setOpen(false)
    pending?.()
  }, [])

  const requestLeave = useCallback((action: LeaveAction) => {
    if (!whenRef.current || bypassRef.current) {
      action()
      return
    }
    pendingRef.current = () => {
      bypassRef.current = true
      action()
      window.setTimeout(() => {
        bypassRef.current = false
      }, 0)
    }
    setOpen(true)
  }, [])

  useEffect(() => {
    if (!when) return
    lockedHref.current =
      window.location.pathname + window.location.search + window.location.hash
    lockedState.current = window.history.state
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [when])

  useEffect(() => {
    const originalPush = window.history.pushState.bind(window.history)
    const originalReplace = window.history.replaceState.bind(window.history)

    const queue = (action: LeaveAction): void => {
      pendingRef.current = action
      if (!openRef.current) setOpen(true)
    }

    const runHistory = (
      original: typeof window.history.pushState,
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ): void => {
      bypassRef.current = true
      original(data, unused, url)
      window.dispatchEvent(
        new PopStateEvent('popstate', { state: window.history.state }),
      )
      window.setTimeout(() => {
        bypassRef.current = false
      }, 0)
    }

    window.history.pushState = ((
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) => {
      if (bypassRef.current || !whenRef.current || sameDocumentTarget(url)) {
        originalPush(data, unused, url)
        return
      }
      queue(() => runHistory(originalPush, data, unused, url))
    }) as typeof window.history.pushState

    window.history.replaceState = ((
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) => {
      if (bypassRef.current || !whenRef.current || sameDocumentTarget(url)) {
        originalReplace(data, unused, url)
        return
      }
      queue(() => runHistory(originalReplace, data, unused, url))
    }) as typeof window.history.replaceState

    const onPopState = (): void => {
      if (bypassRef.current || historyPopClaimed || !whenRef.current) return
      if (!lockedHref.current) return
      historyPopClaimed = true
      window.setTimeout(() => {
        historyPopClaimed = false
      }, 0)
      bypassRef.current = true
      originalPush(lockedState.current, '', lockedHref.current)
      window.dispatchEvent(
        new PopStateEvent('popstate', { state: lockedState.current }),
      )
      bypassRef.current = false
      queue(() => {
        bypassRef.current = true
        window.history.back()
        window.setTimeout(() => {
          bypassRef.current = false
        }, 0)
      })
    }

    window.addEventListener('popstate', onPopState)
    return () => {
      window.history.pushState = originalPush
      window.history.replaceState = originalReplace
      window.removeEventListener('popstate', onPopState)
    }
  }, [])

  return {
    requestLeave,
    modal: (
      <UnsavedChangesModal show={open} onConfirm={confirmLeave} onHide={stay} />
    ),
  }
}

export default useCommunicationsLeaveGuard
